export type CreditAsset = { address: string; symbol: string; decimals: number; priceUsd: number | null };
export type CreditMarket = {
  provider: "morpho"; chainId: 8453; marketId: string; lltv: string; lltvPercent: number;
  loanAsset: CreditAsset; collateralAsset: CreditAsset; borrowApy: number; supplyApy: number;
  liquidityUsd: number; suppliedUsd: number; borrowedUsd: number; utilization: number;
  warnings: Array<{ type: string; level: "YELLOW" | "RED" }>;
  executionEnabled: boolean; supplyEnabled: boolean; fetchedAt: string;
};
export type CreditMarketsResponse = {
  provider: { id: "morpho"; name: "Morpho"; mode: "non-custodial"; chainId: 8453; chainName: "Base" };
  markets: CreditMarket[];
};

export async function fetchCreditMarkets(signal?: AbortSignal): Promise<CreditMarketsResponse> {
  const response = await fetch("/api/v1/credit-markets", { credentials: "same-origin", signal });
  if (!response.ok) throw new Error(`Credit markets request failed (${response.status})`);
  const body = await response.json();
  if (!body?.provider || !Array.isArray(body.markets)) throw new Error("Invalid credit markets response");
  return body as CreditMarketsResponse;
}

export const percent = (value: number) => `${(value * 100).toFixed(2)}%`;
export const compactUsd = (value: number) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 2,
}).format(value);

export function estimateCredit(market: CreditMarket, collateralAmount: number, borrowAmount: number) {
  const collateralPrice = market.collateralAsset.priceUsd || 0;
  const loanPrice = market.loanAsset.priceUsd || 0;
  const collateralValueUsd = Math.max(collateralAmount, 0) * collateralPrice;
  const requestedValueUsd = Math.max(borrowAmount, 0) * loanPrice;
  const protocolLimitUsd = collateralValueUsd * market.lltvPercent / 100;
  const maxBorrowValueUsd = Math.min(protocolLimitUsd, market.liquidityUsd);
  const maxBorrowLoanAssets = loanPrice > 0 ? maxBorrowValueUsd / loanPrice : 0;
  const ltvPercent = collateralValueUsd > 0 ? requestedValueUsd / collateralValueUsd * 100 : 0;
  return {
    collateralValueUsd, requestedValueUsd, maxBorrowValueUsd, maxBorrowLoanAssets, ltvPercent,
    exceedsLltv: requestedValueUsd > 0 && ltvPercent >= market.lltvPercent,
    exceedsLiquidity: requestedValueUsd > market.liquidityUsd,
  };
}
